import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Loader2,
  MailX,
  ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  
  const intakeId = searchParams.get('intake_id');
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!intakeId || !email || !token) {
        setStatus('error');
        setMessage('Invalid unsubscribe link. Please contact the firm directly if you wish to unsubscribe.');
        return;
      }

      try {
        const response = await axios.get(`${API}/public/compliance/unsubscribe`, {
          params: { intake_id: intakeId, email, token }
        });
        setStatus('success');
        setMessage(response.data.message);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Failed to process unsubscribe request. The link may have expired or already been used.');
      }
    };

    processUnsubscribe();
  }, [intakeId, email, token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-slate-200 shadow-lg">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </div>
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email Unsubscribe
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-6">
          {status === 'loading' && (
            <div className="py-8">
              <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Processing your request...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-emerald-800 mb-2">Successfully Unsubscribed</h3>
              <p className="text-slate-600 text-sm">{message}</p>
              <p className="text-slate-500 text-xs mt-4">
                You will no longer receive marketing emails from this firm through IntakePal.
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="py-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Unsubscribe</h3>
              <p className="text-slate-600 text-sm">{message}</p>
            </div>
          )}
          
          <div className="pt-4 border-t">
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Return to Home
              </Button>
            </Link>
          </div>
          
          <p className="text-xs text-slate-400 mt-6">
            Powered by IntakePal - Client Intake Automation
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnsubscribePage;
